import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Spinner, Image } from 'react-bootstrap';
import { GeoAlt, Check2, ChevronDown, Search } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import {
  PRECISION,
  STATE_OPTIONS,
  abbrForStateKey,
  stateKeyForAbbr,
} from '../../utils/homeLocation';
import { fetchAddressDistricts, fetchStateDistricts } from '../../utils/districtsApi';
import './Representatives.css';

// Collects the user's location at whatever precision they are willing to give.
//
// There are now two routes to district accuracy, not one:
//
//   1. Type an address. /districts geocodes it and returns the congressional
//      district plus both state legislative districts, each labelled the way the
//      state itself labels them.
//   2. Pick the districts by hand. /districts/state/{state} lists every district
//      in the state with its current member, so somebody who knows their
//      district -- or recognizes their legislator's name -- gets the same
//      accuracy without an address ever being typed.
//
// Route 2 is the reason the address field stays optional rather than becoming
// required. Only the districts are saved either way; the address is a lookup
// key, not the answer.
//
// Nothing in this modal is required except the state, so no field is marked
// optional. A form where every label says "optional" is saying nothing.
//
// One thing this form never asks for is the county, and it does not need to:
// /districts returns it from the address, and /officials/directory echoes it on
// every response the Representatives page makes. It is worth keeping because it
// is what /elections/candidates filters on -- so both save routes below carry it
// rather than only the one that happens to have just resolved it.

// ─── District select ──────────────────────────────────────────────────────────
// A custom listbox, matching the state picker below, for the same reason: native
// selects open an OS picker that ignores every font rule the app sets.
//
// These lists are long -- New Hampshire's lower chamber has 400 districts -- so
// this one carries a filter. The filter matches the district label and the
// member's name, because "who is my rep" is often easier to answer than "what is
// my district number".

const DistrictSelect = ({ id, label, options, value, onChange, placeholder, open, onOpenChange }) => {
  const { t } = useTranslation('common');
  const [filter, setFilter] = useState('');
  const filterRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFilter('');
      return;
    }
    // Focus the filter rather than the list: on a 400-entry chamber, typing is
    // the only reasonable way in.
    filterRef.current?.focus();
    if (!filterRef.current?.value) selectedRef.current?.scrollIntoView({ block: 'center' });
  }, [open]);

  const selected = useMemo(
    () => options.find((option) => option.district === value) || null,
    [options, value]
  );

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => {
      const haystack = [option.label, ...option.members.map((member) => member.name)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [options, filter]);

  const describeMembers = (option) =>
    option.members
      .map((member) => (member.party ? `${member.name} (${member.party})` : member.name))
      .join(', ');

  return (
    <div className="location-picker-group">
      <span className="location-picker-label" id={`${id}-label`}>
        {label}
      </span>

      <button
        type="button"
        className="location-picker-input location-picker-dropdown-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-label`}
        onClick={() => onOpenChange(!open)}
      >
        <span className="location-picker-dropdown-value">
          <span className={selected ? '' : 'location-picker-placeholder'}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <ChevronDown size={14} />
      </button>

      {open && (
        <>
          <div className="location-picker-filter">
            <Search size={13} className="location-picker-filter-icon" />
            <input
              ref={filterRef}
              type="text"
              className="location-picker-filter-input"
              value={filter}
              placeholder={t('common:filterDistricts', 'Filter by district or name')}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>

          {/* Expands in flow rather than absolutely positioned: the modal body
              scrolls, and an overlay list would be clipped by it on short
              screens. */}
          <ul className="location-picker-dropdown-menu" role="listbox">
            {selected && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="location-picker-dropdown-option location-picker-dropdown-clear"
                  onClick={() => {
                    onChange(null);
                    onOpenChange(false);
                  }}
                >
                  {t('common:clearSelection', 'Clear selection')}
                </button>
              </li>
            )}

            {visible.map((option) => {
              const isSelected = option.district === value;
              return (
                <li key={`${option.district}-${option.order}`}>
                  <button
                    type="button"
                    ref={isSelected ? selectedRef : null}
                    role="option"
                    aria-selected={isSelected}
                    className={`location-picker-dropdown-option ${
                      isSelected ? 'location-picker-dropdown-option-selected' : ''
                    }`}
                    onClick={() => {
                      onChange(option);
                      onOpenChange(false);
                    }}
                  >
                    <span className="location-picker-district-option">
                      <span className="location-picker-district-label">{option.label}</span>
                      {option.members.length > 0 && (
                        // Showing the sitting member turns a number into
                        // something a person can actually recognize.
                        <span className="location-picker-district-member">
                          {describeMembers(option)}
                        </span>
                      )}
                    </span>
                    {isSelected && <Check2 size={16} />}
                  </button>
                </li>
              );
            })}

            {!visible.length && (
              <li className="location-picker-dropdown-empty">
                {t('common:noDistrictsMatch', 'No districts match that.')}
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
};

// ─── Picker ───────────────────────────────────────────────────────────────────

const LocationPicker = ({ show, onHide, currentLocation, onSave }) => {
  const { t } = useTranslation('common');

  const [address, setAddress] = useState('');
  const [stateKey, setStateKey] = useState('');
  const [city, setCity] = useState('');
  const [stateListOpen, setStateListOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [error, setError] = useState(null);

  // Manual district route. Open by default -- see the reset effect below.
  const [districtsOpen, setDistrictsOpen] = useState(true);
  const [stateDistricts, setStateDistricts] = useState(null);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [districtsError, setDistrictsError] = useState(false);
  const [openSelect, setOpenSelect] = useState(null);
  const [picked, setPicked] = useState({ congressional: null, upper: null, lower: null });

  const selectedOptionRef = useRef(null);
  const districtCache = useRef(new Map());

  // currentLocation is read through a ref rather than an effect dependency:
  // useHomeLocation hands back a fresh object on every storage event, so
  // depending on it directly would re-reset the form mid-edit.
  const currentRef = useRef(currentLocation);
  currentRef.current = currentLocation;

  // Reset to whatever is saved each time the modal opens, so a cancelled edit
  // does not leak into the next one.
  useEffect(() => {
    if (!show) return;
    const saved = currentRef.current;

    setAddress(saved?.address || '');
    setStateKey(saved?.stateKey || '');
    setCity(saved?.city || '');
    setStateListOpen(false);
    setResolved(null);
    setError(null);
    setOpenSelect(null);
    setDistrictsError(false);

    // Saved districts pre-fill the manual selects.
    const savedPicks = {
      congressional: saved?.congressionalDistrict
        ? {
            district: String(saved.congressionalDistrict),
            label: saved.congressionalLabel || String(saved.congressionalDistrict),
            members: [],
            order: 0,
          }
        : null,
      upper: saved?.stateUpperDistrict
        ? {
            district: String(saved.stateUpperDistrict),
            label: saved.stateUpperLabel || String(saved.stateUpperDistrict),
            members: [],
            order: 0,
          }
        : null,
      lower: saved?.stateLowerDistrict
        ? {
            district: String(saved.stateLowerDistrict),
            label: saved.stateLowerLabel || String(saved.stateLowerDistrict),
            members: [],
            order: 0,
          }
        : null,
    };

    setPicked(savedPicks);

    // Open every time, not only when the user arrived by this route. Picking a
    // district by hand is the equal of typing an address, and a collapsed
    // section reads as an advanced option rather than the other half of the
    // form. The cost is the state district list being fetched whenever the
    // modal opens with a state already saved; districtCache holds it for the
    // session, so it is one request per state.
    setDistrictsOpen(true);
  }, [show]);

  // Bring the current selection into view when the state list opens rather than
  // dropping the user at Alabama every time.
  useEffect(() => {
    if (!stateListOpen) return;
    selectedOptionRef.current?.scrollIntoView({ block: 'center' });
  }, [stateListOpen]);

  const selectedState = useMemo(
    () => STATE_OPTIONS.find((option) => option.key === stateKey) || null,
    [stateKey]
  );

  const stateAbbr = selectedState?.abbr || abbrForStateKey(stateKey);

  // ─── State district list ────────────────────────────────────────────────────
  // The payload is every district in the state -- 400 rows in New Hampshire --
  // so it is still fetched lazily rather than with the modal: nothing happens
  // until a state is chosen, and the result is cached per state.
  useEffect(() => {
    if (!districtsOpen || !stateAbbr) return undefined;

    const cached = districtCache.current.get(stateAbbr);
    if (cached) {
      setStateDistricts(cached);
      setDistrictsError(false);
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      try {
        setDistrictsLoading(true);
        setDistrictsError(false);
        const result = await fetchStateDistricts(stateAbbr, { signal: controller.signal });
        districtCache.current.set(stateAbbr, result);
        setStateDistricts(result);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setStateDistricts(null);
          setDistrictsError(true);
        }
      } finally {
        if (!controller.signal.aborted) setDistrictsLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [districtsOpen, stateAbbr]);

  const chamberFor = useCallback(
    (key) => stateDistricts?.chambers?.find((chamber) => chamber.key === key) || null,
    [stateDistricts]
  );

  // What was already resolved about this place, and is still about this place.
  //
  // Neither the county nor the place GEOID is asked for anywhere in this form --
  // they come back from /districts or from /officials/directory, which the
  // Representatives page calls on every load. A save that omitted them would
  // clear them, because writeHomeLocation builds an explicit record rather than
  // merging: somebody who typed their address on Monday and then reopened this
  // to correct their city would silently lose the county their candidate list
  // is filtered by.
  //
  // Gated on the state matching. A county belongs to a state, so carrying one
  // across a state change would attach a real county name to the wrong place --
  // which is worse than losing it, since nothing downstream would look wrong.
  const carriedOver = useMemo(() => {
    const saved = currentRef.current;
    if (!saved || !stateKey || saved.stateKey !== stateKey) {
      return { county: null, countyGeoid: null, geoid: null };
    }
    return {
      county: saved.county || null,
      countyGeoid: saved.countyGeoid || null,
      geoid: saved.geoid || null,
    };
  }, [stateKey]);

  // ─── Address route ──────────────────────────────────────────────────────────

  const resolveAddress = async () => {
    const query = address.trim();
    if (query.length < 4) {
      setError(t('common:addressTooShort', 'Enter a full street address.'));
      return;
    }

    setResolving(true);
    setError(null);

    try {
      // include_officials is off: this step only has to prove the address
      // matched. The officeholders are the page's job.
      const result = await fetchAddressDistricts(query, { includeOfficials: false });
      setResolved(result);
    } catch (err) {
      // The bare catch this replaces reported three different failures as one
      // sentence, and "that address could not be matched" was the wrong thing
      // to say about two of them. A reader who was told to check a correct
      // address checks it, retypes it, and gets the same answer.
      console.error('[LocationPicker] district lookup failed', {
        query,
        message: err?.message,
        status: err?.status,
        payload: err?.payload,
      });

      if (err?.status) {
        // The request itself failed. Nothing is wrong with what was typed.
        setError(
          t(
            'common:districtsRequestFailed',
            "Districts couldn't be looked up right now. Try again, or pick your state below."
          )
        );
      } else if (err?.message === 'no state resolved') {
        // A 200 that could not be read. Also not the reader's fault, and
        // telling them to check the address sends them to fix the one thing
        // that is not broken.
        setError(
          t(
            'common:districtsUnreadable',
            "That address was found, but the districts couldn't be read. Pick your state below."
          )
        );
      } else {
        // A failed geocode is not a dead end: the state list below still works,
        // and so does picking districts by hand.
        setError(
          t(
            'common:addressNotFound',
            'That address could not be matched. Check it, or pick your state below.'
          )
        );
      }
    } finally {
      setResolving(false);
    }
  };

  const saveResolved = () => {
    const key = stateKeyForAbbr(resolved.matched.stateAbbr);
    if (!key) {
      setError(t('common:stateNotSupported', 'That state is not supported yet.'));
      return;
    }

    onSave({
      stateKey: key,
      stateAbbr: resolved.matched.stateAbbr,
      address: address.trim(),
      city: resolved.matched.city,

      // The whole of what the address resolved to. The county name is what
      // /elections/candidates filters on and the GEOIDs are what /officials
      // filters on, so all four travel together rather than the name alone.
      county: resolved.matched.county,
      countyGeoid: resolved.matched.countyGeoid,
      geoid: resolved.matched.geoid,

      congressionalDistrict: resolved.congressional?.district || null,
      congressionalLabel: resolved.congressional?.districtLabel || null,

      stateUpperDistrict: resolved.upper?.district || null,
      stateUpperLabel: resolved.upper?.districtLabel || null,
      stateUpperChamberName: resolved.upper?.chamberName || null,

      stateLowerDistrict: resolved.lower?.district || null,
      stateLowerLabel: resolved.lower?.districtLabel || null,
      stateLowerChamberName: resolved.lower?.chamberName || null,
    });

    onHide();
  };

  const saveManual = () => {
    if (!stateKey) {
      setError(t('common:stateRequired', 'Choose a state.'));
      return;
    }

    onSave({
      stateKey,
      stateAbbr,
      city: city.trim() || null,
      address: null,

      // Kept rather than cleared -- see carriedOver above. The address is
      // deliberately dropped on this route (picking by hand is how somebody
      // says "stop using my street address"), but the county is not the
      // address: it is a place name this form never asked for and cannot ask
      // for, and throwing it away would quietly widen the candidate list back
      // to the whole state.
      county: carriedOver.county,
      countyGeoid: carriedOver.countyGeoid,
      geoid: carriedOver.geoid,

      congressionalDistrict: picked.congressional?.district || null,
      congressionalLabel: picked.congressional?.label || null,

      stateUpperDistrict: picked.upper?.district || null,
      stateUpperLabel: picked.upper?.label || null,
      stateUpperChamberName: chamberFor('upper')?.name || null,

      stateLowerDistrict: picked.lower?.district || null,
      stateLowerLabel: picked.lower?.label || null,
      stateLowerChamberName: chamberFor('lower')?.name || null,
    });

    onHide();
  };

  // A resolved district is only claimed when one actually came back. Printing
  // "at-large" as the fallback told Indiana users something false.
  const resolvedSeats = resolved
    ? [resolved.congressional, resolved.upper, resolved.lower].filter(
        (seat) => seat && (seat.districtLabel || seat.district)
      )
    : [];

  return (
    <Modal show={show} onHide={onHide} centered className="location-picker">
      <Modal.Header closeButton>
        <Modal.Title className="location-picker-title">
          {t('common:yourLocation', 'Your district')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="location-picker-help">
          {t(
            'common:locationHelp',
            'Districts are drawn block by block. An address finds yours automatically, or you can pick your districts yourself below. A state alone still shows your senators and the full delegation.'
          )}
        </p>

        <div className="location-picker-group">
          <label className="location-picker-label" htmlFor="location-picker-address">
            {t('common:streetAddress', 'Street address')}
          </label>
          {/* Plain input rather than Form.Control: .form-control carries its own
              Bootstrap focus ring that no theme overrides, which would fight the
              --accent-glow ring used everywhere else. */}
          <input
            id="location-picker-address"
            type="text"
            className="location-picker-input"
            value={address}
            placeholder="50 W 3rd St, Keokuk, IA 52632"
            onChange={(e) => {
              setAddress(e.target.value);
              setResolved(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                resolveAddress();
              }
            }}
          />
          <Button
            id="standardButton"
            className="location-picker-resolve"
            onClick={resolveAddress}
            disabled={resolving || !address.trim()}
          >
            {resolving ? (
              <Spinner animation="border" size="sm" className="themed-spinner" />
            ) : (
              <>
                <GeoAlt className="me-2" />
                {t('common:findDistrict', 'Find my districts')}
              </>
            )}
          </Button>

          {/* Stated where the address is actually typed, not buried in a policy
              page. The wording is deliberately precise, and it got stronger with
              /districts: the address is used once, to resolve the districts, and
              the districts are what every later request is made with. */}
          <p className="location-picker-privacy">
            {t(
              'common:addressPrivacy',
              "Your address is stored only on your device, and is sent to Gov Glance once to look up your districts. After that only the district numbers are used. It isn't saved to an account or shared."
            )}
          </p>
        </div>

        {/* Confirmation step: show what the address resolved to before saving it,
            so a mistyped street is caught here rather than silently returning
            the wrong representatives. */}
        {resolved && (
          <div className="location-picker-resolved">
            <div className="location-picker-resolved-label">
              {t('common:weFoundThis', 'This address is in')}
            </div>
            <div className="location-picker-resolved-place">
              {/* The county is named here because it is now saved and used --
                  it decides which county's candidates come back. Something
                  stored on the strength of this confirmation should appear in
                  the confirmation. */}
              {[resolved.matched.city, resolved.matched.county, resolved.matched.stateAbbr]
                .filter(Boolean)
                .join(', ')}
            </div>

            {resolvedSeats.length > 0 && (
              <ul className="location-picker-resolved-districts">
                {resolvedSeats.map((seat) => (
                  <li key={seat.key}>
                    {/* The state's own label, verbatim. Composing "District N"
                        from a number is how a Delegate gets called a
                        Representative. */}
                    {seat.districtLabel || `${seat.chamberName || ''} ${seat.district}`.trim()}
                  </li>
                ))}
              </ul>
            )}

            <Button id="standardButton" onClick={saveResolved}>
              {t('common:useThisLocation', 'Use this location')}
            </Button>
          </div>
        )}

        <div className="location-picker-divider">
          <span>{t('common:or', 'or')}</span>
        </div>

        <div className="location-picker-group">
          <span className="location-picker-label" id="location-picker-state-label">
            {t('common:state', 'State')}
          </span>

          {/* A custom listbox rather than <select>. Native selects open an OS
              picker that ignores every font rule the app sets -- unreadable in
              device emulation and unstyleable on mobile. This is the same
              pattern .onboarding-city-dropdown already uses. */}
          <button
            type="button"
            className="location-picker-input location-picker-dropdown-toggle"
            aria-haspopup="listbox"
            aria-expanded={stateListOpen}
            aria-labelledby="location-picker-state-label"
            onClick={() => setStateListOpen((open) => !open)}
          >
            <span className="location-picker-dropdown-value">
              <span className="location-picker-flag-slot">
                {selectedState?.flag && (
                  <Image src={selectedState.flag} alt="" className="location-picker-flag" />
                )}
              </span>
              <span className={selectedState ? '' : 'location-picker-placeholder'}>
                {selectedState ? selectedState.name : t('common:chooseState', 'Choose a state')}
              </span>
            </span>
            <ChevronDown size={14} />
          </button>

          {stateListOpen && (
            <ul className="location-picker-dropdown-menu" role="listbox">
              {STATE_OPTIONS.map((option) => {
                const selected = option.key === stateKey;
                return (
                  <li key={option.key}>
                    <button
                      type="button"
                      ref={selected ? selectedOptionRef : null}
                      role="option"
                      aria-selected={selected}
                      className={`location-picker-dropdown-option ${
                        selected ? 'location-picker-dropdown-option-selected' : ''
                      }`}
                      onClick={() => {
                        // Districts belong to a state. Changing the state makes
                        // any picked district a different place entirely, so
                        // they go rather than silently pointing somewhere else.
                        // The same reasoning drops the carried-over county --
                        // see carriedOver, which is keyed on this value.
                        if (option.key !== stateKey) {
                          setPicked({ congressional: null, upper: null, lower: null });
                          setStateDistricts(null);
                        }
                        setStateKey(option.key);
                        setStateListOpen(false);
                        setError(null);
                      }}
                    >
                      <span className="location-picker-dropdown-value">
                        {/* Fixed-width slot rather than a bare image: DC and the
                            territories have no flag asset, and without a
                            reserved space their names would sit out of line
                            with everything else. */}
                        <span className="location-picker-flag-slot">
                          {option.flag && (
                            <Image
                              src={option.flag}
                              alt=""
                              className="location-picker-flag"
                              onError={(e) => {
                                e.currentTarget.style.visibility = 'hidden';
                              }}
                            />
                          )}
                        </span>
                        <span>{option.name}</span>
                      </span>
                      {selected && <Check2 size={16} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Manual districts. Open by default, and still only rendered once a
            state is chosen -- there is nothing to list before that. */}
        {stateKey && (
          <div className="location-picker-districts">
            <button
              type="button"
              className="location-picker-disclosure"
              aria-expanded={districtsOpen}
              onClick={() => setDistrictsOpen((open) => !open)}
            >
              {/* Not common:pickDistrictsYourself -- that key is already in the
                  locale files with the old wording, and i18next only falls back
                  when a key is missing. */}
              <span>{t('common:pickDistricts', 'Pick your districts')}</span>
              <ChevronDown
                size={14}
                className={districtsOpen ? 'location-picker-chevron-open' : ''}
              />
            </button>

            {districtsOpen && (
              <>
                {districtsLoading && (
                  <div className="location-picker-districts-status">
                    <Spinner animation="border" size="sm" className="themed-spinner" />
                  </div>
                )}

                {districtsError && (
                  <div className="location-picker-error">
                    {t(
                      'common:districtsLoadError',
                      "Districts for this state couldn't be loaded. Saving your state still works."
                    )}
                  </div>
                )}

                {stateDistricts && !districtsLoading && (
                  <>
                    {stateDistricts.congressional.length > 0 && (
                      <DistrictSelect
                        id="location-picker-congressional"
                        // Not common:usHouseDistrict -- same reason as above.
                        label={t('common:usCongressionalDistrict', 'U.S. congressional district')}
                        options={stateDistricts.congressional}
                        value={picked.congressional?.district || null}
                        placeholder={t('common:chooseDistrict', 'Choose a district')}
                        open={openSelect === 'congressional'}
                        onOpenChange={(next) => setOpenSelect(next ? 'congressional' : null)}
                        onChange={(option) =>
                          setPicked((current) => ({ ...current, congressional: option }))
                        }
                      />
                    )}

                    {['upper', 'lower'].map((key) => {
                      const chamber = chamberFor(key);
                      if (!chamber) return null;
                      return (
                        <DistrictSelect
                          key={key}
                          id={`location-picker-${key}`}
                          // The chamber's own name, from the API. Nebraska says
                          // Legislature; Virginia says House of Delegates.
                          label={chamber.name}
                          options={chamber.districts}
                          value={picked[key]?.district || null}
                          placeholder={t('common:chooseDistrict', 'Choose a district')}
                          open={openSelect === key}
                          onOpenChange={(next) => setOpenSelect(next ? key : null)}
                          onChange={(option) =>
                            setPicked((current) => ({ ...current, [key]: option }))
                          }
                        />
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>
        )}

        <div className="location-picker-group">
          <label className="location-picker-label" htmlFor="location-picker-city">
            {t('common:city', 'City')}
          </label>
          <input
            id="location-picker-city"
            type="text"
            className="location-picker-input"
            value={city}
            placeholder={t('common:cityPlaceholder', 'Adds your mayor and council')}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>

        {error && <div className="location-picker-error">{error}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button id="standardButton" onClick={onHide}>
          {t('common:cancel', 'Cancel')}
        </Button>
        <Button id="standardButton" onClick={saveManual} disabled={!stateKey}>
          {t('common:save', 'Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LocationPicker;
export { PRECISION };